import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LinkOutlinedIcon from "@material-ui/icons/Link";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import axios from "axios";
import { APIURL } from "src/utils/misc";
import { async_fetch_event } from "src/actions/event.action";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { connect } from "react-redux";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  tableHead: { backgroundColor: "#FAFBFC" },
  tableHeadCell: {
    color: TOKENS.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${TOKENS.border}`,
    padding: "14px 20px",
  },
  tableRow: {
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: "#FAFBFC" },
  },
  tableCell: {
    padding: "16px 20px",
    borderBottom: `1px solid ${TOKENS.divider}`,
    fontSize: 14,
    color: TOKENS.textPrimary,
    verticalAlign: "middle",
  },
  titleCell: { display: "flex", alignItems: "center", gap: 12 },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: TOKENS.divider,
  },
  thumbFallback: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
  },
  titleText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    lineHeight: 1.3,
  },
  titleSub: { fontSize: 12, color: TOKENS.textMuted, marginTop: 2 },
  datePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: TOKENS.divider,
    color: TOKENS.textPrimary,
    whiteSpace: "nowrap",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },
  statusApproved: { backgroundColor: "#ECFDF5", color: "#059669" },
  statusPending: { backgroundColor: "#FEF3C7", color: "#B45309" },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  actions: { display: "flex", gap: 4, justifyContent: "flex-end" },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textSecondary,
    "&:hover": { backgroundColor: TOKENS.divider, color: TOKENS.textPrimary },
  },
  iconBtnDanger: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textSecondary,
    "&:hover": { backgroundColor: "#FEF2F2", color: "#EF4444" },
  },
  emptyState: { padding: "64px 20px", textAlign: "center" },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: 4,
  },
  emptySub: { fontSize: 13, color: TOKENS.textSecondary },
  pagination: {
    borderTop: `1px solid ${TOKENS.border}`,
    "& .MuiTablePagination-toolbar": { paddingLeft: 16, paddingRight: 8 },
    "& .MuiTablePagination-caption": {
      fontSize: 13,
      color: TOKENS.textSecondary,
    },
  },
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr.split("T")[0];
  }
};

function EventListResults({ events = [], async_fetch_event }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);

  const onDelete = async (eventId) => {
    try {
      await axios.delete(`${APIURL}events/${eventId}`);
      window.location.reload();
    } catch (error) {
      toast.error("Action failed!");
    }
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handlePageChange = (_event, newPage) => setPage(newPage);

  const viewEvent = async (eventId) => {
    await async_fetch_event(eventId, navigate);
  };

  const paged = events.slice(page * limit, page * limit + limit);

  return (
    <Card className={classes.card}>
      {events.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIcon}>—</Box>
          <Typography className={classes.emptyTitle}>No events yet</Typography>
          <Typography className={classes.emptySub}>
            When you create an event, it'll show up here.
          </Typography>
        </Box>
      ) : (
        <>
          <PerfectScrollbar>
            <Box style={{ minWidth: 900 }}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>
                      Event
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Location
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Start
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>End</TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Status
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((event) => (
                    <TableRow hover key={event.id} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>
                        <Box className={classes.titleCell}>
                          {event.cover_image_url ? (
                            <Avatar
                              src={event.cover_image_url}
                              variant="rounded"
                              className={classes.thumb}
                            />
                          ) : (
                            <Box className={classes.thumbFallback}>
                              {event.event_name?.[0]?.toUpperCase() || "E"}
                            </Box>
                          )}
                          <Box>
                            <Typography className={classes.titleText}>
                              {event.event_name || "Untitled"}
                            </Typography>
                            {event.country && (
                              <Typography className={classes.titleSub}>
                                {event.country}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {event.district || "—"}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        <span className={classes.datePill}>
                          {formatDate(event.start_date)}
                        </span>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        <span className={classes.datePill}>
                          {formatDate(event.end_date)}
                        </span>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        <span
                          className={`${classes.statusPill} ${
                            event.isApproved
                              ? classes.statusApproved
                              : classes.statusPending
                          }`}
                        >
                          <span
                            className={classes.statusDot}
                            style={{
                              backgroundColor: event.isApproved
                                ? "#10B981"
                                : "#F59E0B",
                            }}
                          />
                          {event.isApproved ? "Approved" : "Pending"}
                        </span>
                      </TableCell>

                      <TableCell className={classes.tableCell} align="right">
                        <Box className={classes.actions}>
                          <Tooltip title="Edit">
                            <IconButton
                              className={classes.iconBtn}
                              onClick={() => viewEvent(event.id)}
                              size="small"
                            >
                              <EditOutlinedIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {event.isApproved && (
                            <CopyToClipboard
                              text={`https://tubayo.com/events/${event.id}`}
                              onCopy={() => toast.success("Link copied!")}
                            >
                              <Tooltip title="Copy public link">
                                <IconButton
                                  className={classes.iconBtn}
                                  size="small"
                                >
                                  <LinkOutlinedIcon style={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </CopyToClipboard>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              className={classes.iconBtnDanger}
                              size="small"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you wish to delete this event?",
                                  )
                                )
                                  onDelete(event.id);
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>
          <TablePagination
            component="div"
            className={classes.pagination}
            count={events.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </Card>
  );
}

export default connect(null, { async_fetch_event })(EventListResults);
